/**
 * SVG Pattern Creation for Map Visualization
 *
 * This module handles the creation of SVG patterns used for hatching effects on the map.
 * It creates diagonal line patterns that are used to visually distinguish buffer zones
 * from other map features.
 *
 * Key responsibilities:
 * - Create SVG pattern definitions for Leaflet maps
 * - Generate gray hatching patterns for field buffers
 * - Generate blue hatching patterns for plot buffers
 * - Handle DOM manipulation for pattern injection
 * - Prevent duplicate pattern creation
 *
 * Patterns created:
 * - field-buffer-hatch: Gray diagonal lines for field edge buffers
 * - plot-buffer-hatch: Blue diagonal lines for plot edge buffers
 *
 * Used by: TestAreasVisualizer.tsx (during map initialization)
 */

export function createSVGPatterns(mapContainer: HTMLElement): void {
  const existingSvg = mapContainer.querySelector('svg defs');

  if (!existingSvg) {
    // Create SVG with pattern definitions
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.width = '0';
    svg.style.height = '0';
    svg.style.pointerEvents = 'none';

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

    // Gray hatching pattern for field buffer
    const grayPattern = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'pattern',
    );
    grayPattern.setAttribute('id', 'field-buffer-hatch');
    grayPattern.setAttribute('patternUnits', 'userSpaceOnUse');
    grayPattern.setAttribute('width', '8');
    grayPattern.setAttribute('height', '8');

    const grayPath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    grayPath.setAttribute('d', 'M0,0 L8,8 M-2,6 L2,10 M6,-2 L10,2');
    grayPath.setAttribute('stroke', '#000');
    grayPath.setAttribute('stroke-width', '2');
    grayPath.setAttribute('stroke-linecap', 'square');

    grayPattern.appendChild(grayPath);
    defs.appendChild(grayPattern);

    // Blue hatching pattern for plot buffer
    const bluePattern = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'pattern',
    );
    bluePattern.setAttribute('id', 'plot-buffer-hatch');
    bluePattern.setAttribute('patternUnits', 'userSpaceOnUse');
    bluePattern.setAttribute('width', '8');
    bluePattern.setAttribute('height', '8');

    const bluePath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    bluePath.setAttribute('d', 'M0,0 L8,8 M-2,6 L2,10 M6,-2 L10,2');
    bluePath.setAttribute('stroke', '#2196f3');
    bluePath.setAttribute('stroke-width', '2');
    bluePath.setAttribute('stroke-linecap', 'square');

    bluePattern.appendChild(bluePath);
    defs.appendChild(bluePattern);

    svg.appendChild(defs);
    mapContainer.appendChild(svg);
  }
}
